import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/contexts/auth.context";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import KorrekturOverviewService from "./korrekturOverview.service";
import type {
	KorrekturOverviewDTO,
	AvailableKlausurDTO,
} from "./korrekturOverview.types";
import { KorrekturStepStatus } from "./korrekturOverview.types";
import ExamCard from "./components/examCard.component";
import StatsOverview from "./components/statsOverview.component";

// Status configuration for the main status cards
const MAIN_STATUS_CONFIG = {
	REVIEW: {
		label: "Bitte überprüfen",
		bgColor: "bg-yellow-200",
		textColor: "text-yellow-800",
	},
	WAITING: {
		label: "Auf vorige Korrektor*in warten",
		bgColor: "bg-blue-200",
		textColor: "text-blue-800",
	},
} as const;

export default function KorrekturOverviewPage() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { klausurId } = useParams<{ klausurId: string }>();
	const [klausurKorrekturen, setKlausurKorrekturen] = useState<
		KorrekturOverviewDTO[]
	>([]);
	const [klausurDetails, setKlausurDetails] =
		useState<AvailableKlausurDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [error, setError] = useState<string | null>(null);
	const korrekturOverviewService = KorrekturOverviewService.getInstance();

	useEffect(() => {
		const fetchData = async () => {
			if (!klausurId) {
				setError("No klausur ID provided");
				setLoading(false);
				return;
			}

			try {
				setLoading(true);

				// Fetch both klausur details and student klausurKorrektur in parallel
				const [klausurDetailsData, studentKlausurKorrekturenData] =
					await Promise.all([
						korrekturOverviewService.getKlausurById(klausurId),
						korrekturOverviewService.getAvailableStudentKlausurKorrekturen(
							klausurId,
						),
					]);

				setKlausurDetails(klausurDetailsData);
				setKlausurKorrekturen(studentKlausurKorrekturenData);
			} catch (err) {
				setError("Fehler beim Laden der Klausuren");
				console.error("Error fetching data:", err);
			} finally {
				setLoading(false);
			}
		};

		if (user && klausurId) {
			fetchData();
		}
	}, [user, klausurId, korrekturOverviewService]);

	if (!user) {
		return <div>You are not logged in</div>;
	}

	if (!klausurKorrekturen) {
		return <div>Keine Klausuren gefunden</div>;
	}

	// Filter klausurKorrektur based on search term, we are just looking for the internal identifier (ID)
	const normalizedSearchTerm = searchTerm.trim().toLowerCase();
	const filteredKlausuren =
		normalizedSearchTerm.length === 0
			? klausurKorrekturen
			: klausurKorrekturen.filter((klausur) => {
					return klausur.internalIdentifier
						.toLowerCase()
						.includes(normalizedSearchTerm);
				});

	// Group klausurKorrektur by status for the wireframe layout
	const reviewKlausuren = []; // LATER WHEN REVIEW IS IMPLEMENTED
	const inProgressKlausuren = filteredKlausuren.filter((k) =>
		k.korrekturSteps.some(
			(step) =>
				step.status === KorrekturStepStatus.IN_PROGRESS && step.isCurrentUser,
		),
	);
	const notStartedKlausuren = filteredKlausuren.filter((k) =>
		k.korrekturSteps.some(
			(step) =>
				step.status === KorrekturStepStatus.NOT_STARTED && step.isCurrentUser,
		),
	);
	const completedKlausuren = filteredKlausuren.filter((k) =>
		k.korrekturSteps.some(
			(step) =>
				step.status === KorrekturStepStatus.COMPLETED && step.isCurrentUser,
		),
	);

	const handleExamClick = (klausur: KorrekturOverviewDTO) => {
		// Navigate to specific exam correction
		console.log("Navigate to exam:", klausur.id);

		//find the current user step
		const currentUserStep = klausur.korrekturSteps.find(
			(step) => step.isCurrentUser === true,
		);

		if (currentUserStep) {
			navigate(`/korrektur/${currentUserStep?.id}`);
		}

		return;
	};

	if (loading) {
		return (
			<div className="p-6">
				<div className="text-center">Loading exam corrections...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-6">
				<div className="text-center text-red-600">{error}</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen p-6">
			<div className="max-w-6xl mx-auto space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<Button
						variant="ghost"
						onClick={() => navigate("/dashboard")}
						className="flex items-center gap-2 text-gray-600"
					>
						<ArrowLeft className="w-4 h-4" />
						Zurück
					</Button>
					<div className="px-2 py-1 rounded-lg">
						<h1 className="text-lg font-medium text-center">
							{klausurDetails ? klausurDetails.title : "Klausurname"}
						</h1>
					</div>
					<div className="w-16" /> {/* Spacer for centering */}
				</div>

				{/* Main Status Cards */}
				<div className="bg-white p-3 rounded-lg border border-gray-200">
					{/* Priority Actions - Only show if there are items */}
					{reviewKlausuren.length > 0 && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
							{reviewKlausuren.length > 0 && (
								<Card className="py-2 bg-yellow-50 border border-yellow-200">
									<CardContent className="p-3 text-center">
										<div className="text-sm text-yellow-700 mb-2">
											{MAIN_STATUS_CONFIG.REVIEW.label}
										</div>
										<div className="text-2xl font-semibold text-yellow-900">
											{reviewKlausuren.length}
										</div>
									</CardContent>
								</Card>
							)}
							{/* 
							{waitingKlausuren.length > 0 && (
								<Card className="py-2 bg-blue-50 border border-blue-200">
									<CardContent className="p-3 text-center">
										<div className="text-sm text-blue-700 mb-2">
											{MAIN_STATUS_CONFIG.WAITING.label}
										</div>
										<div className="text-2xl font-semibold text-blue-900">
											{waitingKlausuren.length}
										</div>
									</CardContent>
								</Card>
							)} */}
						</div>
					)}

					{/* Status Overview */}
					<div
						className={`border-gray-100 ${reviewKlausuren.length > 0 ? "pt-4 border-t" : "pt-0"}`}
					>
						<StatsOverview
							title="Korrekturübersicht"
							className="bg-transparent border-0 p-0"
							items={[
								{
									label: "Nicht gestartet",
									value: notStartedKlausuren.length,
									valueClassName: "text-blue-600",
								},
								{
									label: "In Bearbeitung",
									value: inProgressKlausuren.length,
									valueClassName: "text-amber-600",
								},
								{
									label: "Abgeschlossen",
									value: completedKlausuren.length,
									valueClassName: "text-emerald-600",
								},
							]}
						/>
					</div>
				</div>

				{/* Search and Filter Bar */}
				<div className="flex gap-4 items-center">
					<div className="flex-1 relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
						<Input
							type="text"
							placeholder="Suche nach ID"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-10 bg-white"
						/>
					</div>
				</div>

				{/* Exam Cards Grid */}
				<div className="space-y-6">
					{/* In Progress Section */}
					{filteredKlausuren.length > 0 ? (
						<div>
							<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
								<div className="w-2 h-2 bg-blue-500 rounded-full" />
								In Bearbeitung
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
								{filteredKlausuren
									.slice()
									.sort((a, b) => {
										// Get current user step for each klausur
										const aStep = a.korrekturSteps.find(
											(step) => step.isCurrentUser,
										);
										const bStep = b.korrekturSteps.find(
											(step) => step.isCurrentUser,
										);

										// Helper function to get priority (lower number = higher priority)
										const getPriority = (
											step:
												| { status: KorrekturStepStatus; locked: boolean }
												| undefined,
										) => {
											if (!step) return 999; // fallback

											if (step.status === KorrekturStepStatus.IN_PROGRESS)
												return 1;
											if (
												step.status === KorrekturStepStatus.NOT_STARTED &&
												!step.locked
											)
												return 2;
											if (
												step.status === KorrekturStepStatus.NOT_STARTED &&
												step.locked
											)
												return 3; // gesperrt
											if (step.status === KorrekturStepStatus.COMPLETED)
												return 4;

											return 999; // fallback
										};

										const aPriority = getPriority(aStep);
										const bPriority = getPriority(bStep);

										return aPriority - bPriority;
									})
									.map((klausur) => (
										<ExamCard
											key={klausur.id}
											klausur={klausur}
											onClick={() => handleExamClick(klausur)}
										/>
									))}
							</div>
						</div>
					) : (
						<div className="text-sm text-gray-600 text-center py-8">
							Keine passenden Klausuren gefunden.
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
